import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, AdminLayout } from "./AdminLayout";
import { TipTapEditor, MediaLibrary } from "../../components/admin";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminBlogs = () => {
  const { token } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [topics, setTopics] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [mediaInsertCallback, setMediaInsertCallback] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    content: "",
    excerpt: "",
    featured_image: "",
    topics: [],
    is_published: false,
  });

  const handleOpenMediaLibrary = (insertCallback) => {
    setMediaInsertCallback(() => insertCallback);
    setMediaLibraryOpen(true);
  };

  const handleMediaSelect = (url, position) => {
    if (mediaInsertCallback) mediaInsertCallback(url, position);
    setMediaLibraryOpen(false);
    setMediaInsertCallback(null);
  };

  const handleMediaClose = () => {
    setMediaLibraryOpen(false);
    setMediaInsertCallback(null);
  };

  useEffect(() => {
    fetchBlogs();
    fetchTopics();
  }, []);

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${API}/blogs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlogs(res.data);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  };

  const fetchTopics = async () => {
    try {
      const res = await axios.get(`${API}/topics`);
      setTopics(res.data);
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
  };

  const handleEdit = (blog) => {
    setCurrentBlog(blog);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      content: blog.content,
      excerpt: blog.excerpt,
      featured_image: blog.featured_image || "",
      topics: blog.topics || [],
      is_published: blog.is_published,
    });
    setIsEditing(true);
  };

  const handleNew = () => {
    setCurrentBlog(null);
    setFormData({
      title: "",
      slug: "",
      content: "",
      excerpt: "",
      featured_image: "",
      topics: [],
      is_published: false,
    });
    setIsEditing(true);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: currentBlog ? formData.slug : generateSlug(title),
    });
  };

  const handleTopicToggle = (topicSlug) => {
    const newTopics = formData.topics.includes(topicSlug)
      ? formData.topics.filter((t) => t !== topicSlug)
      : [...formData.topics, topicSlug];
    setFormData({ ...formData, topics: newTopics });
  };

  const handleSave = async () => {
    try {
      if (currentBlog) {
        await axios.put(`${API}/blogs/${currentBlog.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Blog updated successfully");
      } else {
        await axios.post(`${API}/blogs`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Blog created successfully");
      }
      setIsEditing(false);
      fetchBlogs();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save blog");
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm("Are you sure you want to delete this blog post?")) return;
    try {
      await axios.delete(`${API}/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Blog deleted");
      fetchBlogs();
    } catch (error) {
      toast.error("Failed to delete blog");
    }
  };

  const togglePublish = async (blog) => {
    try {
      await axios.put(
        `${API}/blogs/${blog.id}`,
        { is_published: !blog.is_published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBlogs();
      toast.success(blog.is_published ? "Blog unpublished" : "Blog published");
    } catch (error) {
      toast.error("Failed to update blog");
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-blogs">
        <div className="flex items-center justify-between">
          <h1 className="heading-3">Blog Posts</h1>
          <button onClick={handleNew} className="btn-primary" data-testid="new-blog-btn">
            <Plus size={18} className="mr-2" />
            New Post
          </button>
        </div>

        {/* Blog List */}
        <div className="glass-card">
          {blogs.length === 0 ? (
            <p className="text-its-offwhite/60 text-center py-8">No blog posts yet</p>
          ) : (
            <div className="space-y-3">
              {blogs.map((blog) => (
                <div
                  key={blog.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-its-teal/50 border border-its-teal-light/10"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-white truncate">{blog.title}</h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          blog.is_published
                            ? "bg-green-500/20 text-green-400"
                            : "bg-yellow-500/20 text-yellow-400"
                        }`}
                      >
                        {blog.is_published ? "Published" : "Draft"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-its-offwhite/60">
                      <span>{formatDate(blog.created_at)}</span>
                      {blog.topics?.length > 0 && (
                        <span className="flex gap-1">
                          {blog.topics.map((t) => (
                            <span key={t} className="px-1.5 py-0.5 bg-its-teal-secondary/30 rounded">
                              {t}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => togglePublish(blog)}
                      className="p-2 text-its-offwhite/60 hover:text-white transition-colors"
                      title={blog.is_published ? "Unpublish" : "Publish"}
                    >
                      {blog.is_published ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button
                      onClick={() => handleEdit(blog)}
                      className="p-2 text-its-offwhite/60 hover:text-its-gold transition-colors"
                      data-testid={`edit-blog-${blog.id}`}
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(blog.id)}
                      className="p-2 text-its-offwhite/60 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent
            className="max-w-4xl max-h-[90vh] overflow-y-auto bg-its-teal-dark border-its-teal-light/20"
            onInteractOutside={(e) => { if (mediaLibraryOpen) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (mediaLibraryOpen) e.preventDefault(); }}
            onEscapeKeyDown={(e) => { if (mediaLibraryOpen) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle className="text-white">
                {currentBlog ? "Edit Blog Post" : "New Blog Post"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    className="form-input"
                    data-testid="blog-title-input"
                  />
                </div>
                <div>
                  <label className="form-label">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Excerpt</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="form-input"
                  rows={2}
                />
              </div>

              <div>
                <label className="form-label">Featured Image URL</label>
                <input
                  type="text"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  className="form-input"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="form-label">Topics</label>
                <div className="flex flex-wrap gap-2">
                  {topics.map((topic) => (
                    <button
                      key={topic.id}
                      type="button"
                      onClick={() => handleTopicToggle(topic.slug)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.topics.includes(topic.slug)
                          ? "bg-its-gold text-its-teal"
                          : "bg-its-teal-secondary/30 text-its-offwhite hover:bg-its-teal-secondary/50"
                      }`}
                    >
                      {topic.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="form-label">Content</label>
                <TipTapEditor
                  content={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Write your blog post..."
                  onOpenMediaLibrary={handleOpenMediaLibrary}
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    className="w-4 h-4 rounded border-its-teal-light/30"
                  />
                  <span className="text-sm text-its-offwhite">Publish immediately</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button onClick={handleSave} className="btn-primary" data-testid="save-blog-btn">
                  {currentBlog ? "Update Post" : "Create Post"}
                </button>
                <button onClick={() => setIsEditing(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Media Library — rendered OUTSIDE Radix Dialog to avoid pointer-events/focus trap */}
        <MediaLibrary
          isOpen={mediaLibraryOpen}
          onClose={handleMediaClose}
          onSelect={handleMediaSelect}
          token={token}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminBlogs;
